import '@okta/okta-auth-js/polyfill';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_CONFIG, FIREWEATHER_API_URL} from './env';
import {sleep} from './layer-manager'

const config = OKTA_CONFIG

export var authClient;
export var appState = {};

// A list of paths that are restricted to users with "FireWeather_Admin" in their Okta access token groups
var adminOnlyPages = ["/admin.html"];

var url = new URL(window.location.href);
config.error =  url.searchParams.get('error'); 

function updateAppState(props) {
    Object.assign(appState, props);
}

function showError(error) {
  console.error(error);
  const containerElem = document.getElementById('error');
  containerElem.style.display = 'block';
  var node = document.createElement('DIV');
  node.innerText = error.message || typeof error === 'string' ? error : JSON.stringify(error, null, 2);
  containerElem.appendChild(node);
}

// Modifies the "authState" object before it is emitted. This is a chance to add custom logic and extra properties.
function transformAuthState(_authClient, authState) {
    var promise = Promise.resolve(authState);
  
    if (authState.accessToken && authState.idToken) {
      authState.hasTokens = true;
    }
  
    // With this option we require the user to have not only valid tokens, but a valid Okta SSO session as well
    if (config.requireUserSession && authState.hasTokens) {
      promise = promise.then(function() {
        return appState.userInfo || authClient.token.getUserInfo();
      }).then(function(value) {
        updateAppState({ userInfo: value });
        authState.isAuthenticated = authState.isAuthenticated && !!appState.userInfo;
        return authState;
      });
    }
  
    return promise;
}

// The `OktaAuth` constructor can throw if the config is malformed
function createAuthClient() {
    try {
      authClient = new OktaAuth({
        issuer: config.issuer,
        clientId: config.clientId,
        redirectUri: config.redirectUri,
        scopes: config.scopes,
        tokenManager: {
          storage: config.storage
        },
        transformAuthState,
        recoveryToken: config.recoveryToken
      });
      if (config.startService) {
        authClient.start();
      }
    } catch (error) {
      console.log(error);
    }
}

//authClient and its values won't be accessible the instant the page loads
//we need to wait for it to exist before we can access it without errors
export async function getAuthClient(authClient){
  while (!authClient ||
         !authClient.authStateManager ||
         !authClient.authStateManager._authState||
         !authClient.authStateManager._authState.accessToken) {
      await sleep(100);
  }
  return authClient;
}

function renderUnauthenticated() {
    // The `handleLoginRedirect` may have failed. An error or remediation should be shown.
    if (!authClient || authClient.token.isLoginRedirect()) {
      return;
    }
}

function shouldRedirectToGetTokens(authState) {
    if (authState.isAuthenticated) {
      return false;
    }
  
    // Special handling for memory-based token storage.
    // There will be a redirect on each page load to acquire fresh tokens.
    if (config.storage === 'memory' || config.getTokens) {
  
      // Callback from Okta triggered by `redirectToGetTokens`
      // If the callback has errored, it means there is no Okta session and we should begin a new auth flow
      // This condition breaks a potential infinite rediret loop
      if (config.error === 'login_required') {
        return false;
      }

      if (config.error === 'access_denied') {
        showError(config.error)
        return false;
      }
  
      // AuthState error. This can happen when an exception is thrown inside transformAuthState.
      // Return false to break a potential infinite loop
      if (authState.error) {
        return false;
      }
  
      // Don't acquire tokens during signing out
      if (appState.signedOut) {
        return false;
      }
  
      // Call Okta to get tokens. Okta will redirect back to this app
      // The callback is handled by `handleLoginRedirect` which will call `renderApp` again
      return true;
    }
}

function redirectToGetTokens(additionalParams) {
    if (config.state.appUri == null){
        config.state.appUri = window.location.href
    }
    // If an Okta SSO exists, the redirect will return a code which can be exchanged for tokens
    // If a session does not exist, it will return with "error=login_required"
    authClient.token.getWithRedirect(Object.assign({
      state: JSON.stringify(config.state),
      prompt: 'consent' // none - do not show Okta hosted login page, instead redirect back with error
    }, additionalParams));
}

function startApp() {
    // Calculates initial auth state and fires change event for listeners
    // Also starts the token auto-renew service
    authClient.start();
}
function endAuthFlow(tokens, state) {  
    var appUri = state.appUri != undefined?state.appUri: config.appUri;
    config.state.appUri = appUri
    // parseFromUrl clears location.search. There may also be a leftover "error" param from the auth flow.
    // Replace state with the canonical app uri so the page can be reloaded cleanly.
    history.replaceState(null, '', appUri);
  
    // Store tokens. This will update the auth state and we will re-render
    authClient.tokenManager.setTokens(tokens);
    location.reload()
}

function handleLoginRedirect() {
    // If the URL contains a code, `parseFromUrl` will grab it and exchange the code for tokens
    return authClient.token.parseFromUrl().then(function (res) {
        var state =  res.state != undefined? JSON.parse(res.state):{}
        endAuthFlow(res.tokens, state); // save tokens
    }).catch(function(error) {
        console.log(error); 
    });
}

function showLoading() {    
    if (document.getElementById('loadingOverlay') !=null) {
        document.getElementById('loadingOverlay').style.display = 'block';
    }
    else if (document.getElementById('ajax_loader') !=null) {
        document.getElementById('ajax_loader').style.display = 'block';
    }    
    
    if (document.getElementById('secure-content') !=null) {
        document.getElementById('secure-content').style.display = 'none';
    }
}

function hideLoading() {
    if (document.getElementById('loadingOverlay') !=null) {
        document.getElementById('loadingOverlay').style.display = 'none';
    }
    else if (document.getElementById('ajax_loader') !=null) {
        document.getElementById('ajax_loader').style.display = 'none';
    }      
    if (document.getElementById('secure-content') !=null) {
        document.getElementById('secure-content').style.display = 'block';
    }
}

function renderApp() {
    const authState = authClient.authStateManager.getAuthState();
    // Setting auth state is an asynchronous operation. If authState is not available yet, render in the loading state
    if (!authState) {
      return showLoading();
    }
    hideLoading();
    if (authState.isAuthenticated) {
        if (document.getElementById('logout') !=null) {
            $("#logout").on("click", logout);
            document.getElementById('logout').style.display = 'block';        
        }
        if (document.getElementById('welcome-user') !=null) {
            document.getElementById('welcome-user').textContent = `Welcome ${appState.userInfo.email}`;
        }
    }  
    // Default: Unauthenticated state
    return renderUnauthenticated();
}

// Enable logout
export function logout(){
    const clearTokensBeforeRedirect = config.storage === 'memory';
    authClient.signOut({ clearTokensBeforeRedirect });
}

function URLIsAdminRestricted(path){
  for (let restrictedPath of adminOnlyPages) {
    if (path.toString().toLowerCase() === restrictedPath.toString().toLowerCase()){
      return true;
    }
  }
  return false;
}

export function addLoginCheck() {
    var hasValidConfig = !!(config.issuer && config.clientId);
    if (!hasValidConfig) {
      console.log('Config missing `issuer` and `clientId`');
      return renderUnauthenticated();
    }
  
    // Config is valid
    createAuthClient();
  
    // Subscribe to authState change event. Logic based on authState is done here.
    authClient.authStateManager.subscribe(function(authState) {
      if (!authState.isAuthenticated) {
        // If not authenticated, reset values related to user session
        updateAppState({ userInfo: null });
      }
      
      //FWP-43: temporarily removed until admin access tokens are sorted out.
      // If the page is admin-restricted, checks the user's access token groups for admin privileges
      // if(URLIsAdminRestricted(window.location.pathname) && (authState.accessToken != null)) {
      //   if(!authState.accessToken.claims.groups.includes("FireWeather_Admin")){
      //     window.location.replace("/index.html?AdminPageAccessDenied"); // Redirects user to the homepage, with a notification that they do not have the permissions to access a restricted page
      //   }
      // }

      // If there is an active session, we can get tokens via a redirect
      // This allows in-memory token storage without prompting for credentials on each page load
      if (shouldRedirectToGetTokens(authState)) {
        return redirectToGetTokens();
      }
  
      // Render app based on the new authState
      renderApp();
    });
  
    // During the OIDC auth flow, the app will receive a code passed to the `redirectUri`
    // This event occurs *in the middle* of an authorization flow
    // The callback handler logic should happen *before and instead of* any other auth logic
    // In most apps this callback will be handled by a special route
    // For SPA apps like this, with no routing or hash-based routing, the callback is handled in the main function
    // Once the callback is handled, the app can startup normally
    if (authClient.token.isLoginRedirect()) {
      return handleLoginRedirect().then(function() {
        startApp();
      });
    } 
    // Normal app startup
    startApp();
}
function bindHeaderToRequests(){  
  // try {  

  //     XMLHttpRequest.prototype.open = (function(open) {
  //         return function(method,url,async) {
  //             open.apply(this,arguments);
  //             try{
  //                 if(url.startsWith("https://") || url.startsWith("http://")){
  //                   var currentUrl = new URL(url);
  //                   var apiUrl = new URL(FIREWEATHER_API_URL)
  //                   if (currentUrl.origin == apiUrl.origin && !requestHeaderDebouncer){
  //                     // Debouncer to stop two authorisation headers being added.
  //                     requestHeaderDebouncer = true;
  //                     setTimeout(() => {
  //                       requestHeaderDebouncer = false;
  //                     }, 100);

  //                     const { idToken, accessToken, refreshToken } = authClient.tokenManager.getTokensSync();
  //                     this.setRequestHeader('Authorization', `Bearer ${accessToken.accessToken}`);
  //                   }
  //                 }
  //             }
  //             catch(err) {
  //                 console.log(err)
  //             }
  //         };
  //     })(XMLHttpRequest.prototype.open);
  //   } 
  //   catch(err) {
  //       console.log(err)
  //   }       
    try {
       if (typeof require !== 'undefined' && typeof esri !== 'undefined'){
          __non_webpack_require__(["esri/config", "esri/request", "dojo/ready"], function(esriConfig, esriRequest) {
            var apiUrl = new URL(FIREWEATHER_API_URL)    
            esriConfig.defaults.io.corsEnabledServers.push({
              host: apiUrl.host
            })
            esriRequest.setRequestPreCallback(function (ioArgs){
              try{ 
                console.log(ioArgs.url)     
                if(ioArgs.url.startsWith("https://") || ioArgs.url.startsWith("http://")){
                  var currentUrl = new URL(ioArgs.url);
                  var apiUrl = new URL(FIREWEATHER_API_URL)
                  if (currentUrl.origin == apiUrl.origin){  
                    const { idToken, accessToken, refreshToken } = authClient.tokenManager.getTokensSync();
                    ioArgs.headers = ioArgs.headers || {}; // make sure headers are not null
                    ioArgs.headers["Authorization"] = "Bearer " + accessToken.accessToken
                  }
                }
              }
              catch(err) {
                  console.log(err)
              }
              finally {
                  return ioArgs;
              }
            })            
          });                    
        }
    }
    catch(err) {
        console.log(err)
    }      
}

addLoginCheck()
bindHeaderToRequests()